import React, { useState } from 'react';
import Button from '../../common/Button/Button';
import scopeFormSchema from '../../../utils/scopeFormSchema';
import styles from './ScopeForm.module.css';

const isRepeatableSection = (section) =>
  section.type === 'repeatable block' || section.type === 'repeatable table rows';

// Is a single field value empty for the purposes of "what's left to fill"?
const isEmptyValue = (field, value) => {
  if (field.type === 'multi-select tags' || field.type === 'multi-select checkboxes') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (field.type === 'editable checklist') {
    return ((value?.items?.length || 0) + (value?.customItems?.length || 0)) === 0;
  }
  return value === undefined || value === null || String(value).trim() === '';
};

const isRowEmpty = (fields, row) => fields.every((f) => isEmptyValue(f, row?.[f.name]));

/**
 * Snapshot of what the extraction left blank, taken once at mount. Used by
 * "remaining only" mode so the mentor sees a stable set of gaps to fill
 * (fields don't disappear the moment they start typing).
 *
 * Returns:
 *   fixed:      { [sectionName]: Set<emptyFieldName> }
 *   repeatable: { [sectionName]: boolean }  // true = extraction produced no rows
 */
const computeEmptySnapshot = (state) => {
  const fixed = {};
  const repeatable = {};
  scopeFormSchema.sections.forEach((section) => {
    if (isRepeatableSection(section)) {
      const rows = state[section.section] || [];
      repeatable[section.section] = !rows.some((row) => !isRowEmpty(section.fields, row));
    } else {
      const emptyNames = new Set();
      section.fields.forEach((f) => {
        if (isEmptyValue(f, state[section.section]?.[f.name])) emptyNames.add(f.name);
      });
      fixed[section.section] = emptyNames;
    }
  });
  return { fixed, repeatable };
};

const ScopeForm = ({ initialValues = null, onSubmit, onCancel, remainingOnly = false }) => {
  // Build initial state from schema or existing values
  const buildInitialState = () => {
    if (initialValues) return initialValues;

    const state = {};
    scopeFormSchema.sections.forEach(section => {
      if (section.type === 'repeatable block' || section.type === 'repeatable table rows') {
        // Start with one empty row
        state[section.section] = [buildEmptyRow(section.fields)];
      } else {
        state[section.section] = {};
        section.fields.forEach(field => {
          if (field.type === 'multi-select tags' || field.type === 'multi-select checkboxes') {
            state[section.section][field.name] = [];
          } else if (field.type === 'editable checklist') {
            state[section.section][field.name] = {
              items: (field.default_items || []).map(item => ({ text: item, checked: false })),
              customItems: []
            };
          } else if (field.type === 'number input') {
            state[section.section][field.name] = '';
          } else {
            state[section.section][field.name] = '';
          }
        });
      }
    });
    return state;
  };

  const buildEmptyRow = (fields) => {
    const row = {};
    fields.forEach(field => {
      row[field.name] = '';
    });
    return row;
  };

  const [formData, setFormData] = useState(buildInitialState);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  // Frozen at mount: which fields the extraction left blank.
  const [emptySnapshot] = useState(() => computeEmptySnapshot(buildInitialState()));

  // --- Updaters ---

  const updateField = (sectionName, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [fieldName]: value
      }
    }));
  };

  const toggleMultiSelect = (sectionName, fieldName, option) => {
    setFormData(prev => {
      const current = prev[sectionName][fieldName] || [];
      const updated = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return {
        ...prev,
        [sectionName]: { ...prev[sectionName], [fieldName]: updated }
      };
    });
  };

  const toggleChecklistItem = (sectionName, fieldName, index, isCustom = false) => {
    setFormData(prev => {
      const checklist = { ...prev[sectionName][fieldName] };
      const key = isCustom ? 'customItems' : 'items';
      checklist[key] = checklist[key].map((item, i) =>
        i === index ? { ...item, checked: !item.checked } : item
      );
      return {
        ...prev,
        [sectionName]: { ...prev[sectionName], [fieldName]: checklist }
      };
    });
  };

  const addChecklistItem = (sectionName, fieldName, text) => {
    if (!text.trim()) return;
    setFormData(prev => {
      const checklist = { ...prev[sectionName][fieldName] };
      checklist.customItems = [...checklist.customItems, { text: text.trim(), checked: false }];
      return {
        ...prev,
        [sectionName]: { ...prev[sectionName], [fieldName]: checklist }
      };
    });
    setNewChecklistItem('');
  };

  const removeChecklistItem = (sectionName, fieldName, index) => {
    setFormData(prev => {
      const checklist = { ...prev[sectionName][fieldName] };
      checklist.customItems = checklist.customItems.filter((_, i) => i !== index);
      return {
        ...prev,
        [sectionName]: { ...prev[sectionName], [fieldName]: checklist }
      };
    });
  };

  // Repeatable rows
  const updateRepeatableField = (sectionName, rowIndex, fieldName, value) => {
    setFormData(prev => {
      const rows = [...prev[sectionName]];
      rows[rowIndex] = { ...rows[rowIndex], [fieldName]: value };
      return { ...prev, [sectionName]: rows };
    });
  };

  const addRepeatableRow = (section) => {
    setFormData(prev => ({
      ...prev,
      [section.section]: [...prev[section.section], buildEmptyRow(section.fields)]
    }));
  };

  const removeRepeatableRow = (sectionName, index) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: prev[sectionName].filter((_, i) => i !== index)
    }));
  };

  // --- Renderers ---

  const renderField = (field, sectionName) => {
    const value = formData[sectionName]?.[field.name];

    switch (field.type) {
      case 'text input':
        return (
          <input
            type="text"
            className={styles.textInput}
            value={value || ''}
            onChange={(e) => updateField(sectionName, field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}...`}
          />
        );

      case 'number input':
        return (
          <input
            type="number"
            className={styles.numberInput}
            value={value || ''}
            onChange={(e) => updateField(sectionName, field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}...`}
          />
        );

      case 'textarea':
        return (
          <textarea
            className={styles.textarea}
            value={value || ''}
            onChange={(e) => updateField(sectionName, field.name, e.target.value)}
            rows="3"
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}...`}
          />
        );

      case 'dropdown':
        return (
          <select
            className={styles.select}
            value={value || ''}
            onChange={(e) => updateField(sectionName, field.name, e.target.value)}
          >
            <option value="">-- Select {field.name} --</option>
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multi-select tags':
        return (
          <div className={styles.tagsContainer}>
            {field.options.map(opt => (
              <span
                key={opt}
                className={`${styles.tag} ${(value || []).includes(opt) ? styles.tagSelected : ''}`}
                onClick={() => toggleMultiSelect(sectionName, field.name, opt)}
              >
                {opt}
              </span>
            ))}
          </div>
        );

      case 'multi-select checkboxes':
        return (
          <div className={styles.checkboxGroup}>
            {field.options.map(opt => (
              <label key={opt} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={(value || []).includes(opt)}
                  onChange={() => toggleMultiSelect(sectionName, field.name, opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'editable checklist':
        return (
          <div className={styles.checklist}>
            {field.note && <p className={styles.checklistNote}>{field.note}</p>}
            <div className={styles.checklistItems}>
              {value?.items?.map((item, i) => (
                <label key={`default-${i}`} className={styles.checklistItem}>
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggleChecklistItem(sectionName, field.name, i, false)}
                  />
                  <span>{item.text}</span>
                </label>
              ))}
              {value?.customItems?.map((item, i) => (
                <div key={`custom-${i}`} className={styles.checklistItemCustom}>
                  <label className={styles.checklistItem}>
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => toggleChecklistItem(sectionName, field.name, i, true)}
                    />
                    <span>{item.text}</span>
                  </label>
                  <button
                    type="button"
                    className={styles.removeItemBtn}
                    onClick={() => removeChecklistItem(sectionName, field.name, i)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.addChecklistRow}>
              <input
                type="text"
                className={styles.textInput}
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                placeholder="Add a custom step..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addChecklistItem(sectionName, field.name, newChecklistItem);
                  }
                }}
              />
              <Button
                type="button"
                size="small"
                onClick={() => addChecklistItem(sectionName, field.name, newChecklistItem)}
              >
                + Add
              </Button>
            </div>
          </div>
        );

      default:
        return <input type="text" className={styles.textInput} value={value || ''} onChange={(e) => updateField(sectionName, field.name, e.target.value)} />;
    }
  };

  const renderRepeatableSection = (section) => {
    const rows = formData[section.section] || [];
    const isTable = section.type === 'repeatable table rows';

    if (isTable) {
      return (
        <div className={styles.repeatableTable}>
          <table className={styles.table}>
            <thead>
              <tr>
                {section.fields.map(f => <th key={f.name}>{f.name}</th>)}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {section.fields.map(field => (
                    <td key={field.name}>
                      {field.type === 'dropdown' ? (
                        <select
                          className={styles.tableSelect}
                          value={row[field.name] || ''}
                          onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                        >
                          <option value="">--</option>
                          {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          className={styles.tableTextarea}
                          value={row[field.name] || ''}
                          onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                          rows="2"
                        />
                      ) : (
                        <input
                          type={field.type === 'number input' ? 'number' : 'text'}
                          className={styles.tableInput}
                          value={row[field.name] || ''}
                          onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                          placeholder={field.placeholder || ''}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeRowBtn}
                        onClick={() => removeRepeatableRow(section.section, rowIndex)}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className={styles.addRowBtn} onClick={() => addRepeatableRow(section)}>
            + Add Row
          </button>
        </div>
      );
    }

    // Repeatable block (card-style)
    return (
      <div className={styles.repeatableBlocks}>
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.repeatableBlock}>
            <div className={styles.blockHeader}>
              <span className={styles.blockNumber}>#{rowIndex + 1}</span>
              {rows.length > 1 && (
                <button
                  type="button"
                  className={styles.removeRowBtn}
                  onClick={() => removeRepeatableRow(section.section, rowIndex)}
                >
                  ✕ Remove
                </button>
              )}
            </div>
            {section.fields.map(field => (
              <div key={field.name} className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{field.name}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    className={styles.textarea}
                    value={row[field.name] || ''}
                    onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                    rows="2"
                    placeholder={field.placeholder || ''}
                  />
                ) : field.type === 'dropdown' ? (
                  <select
                    className={styles.select}
                    value={row[field.name] || ''}
                    onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number input' ? 'number' : 'text'}
                    className={styles.textInput}
                    value={row[field.name] || ''}
                    onChange={(e) => updateRepeatableField(section.section, rowIndex, field.name, e.target.value)}
                    placeholder={field.placeholder || ''}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
        <button type="button" className={styles.addRowBtn} onClick={() => addRepeatableRow(section)}>
          + Add {section.section.replace(/s$/, '')}
        </button>
      </div>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(JSON.stringify(formData));
  };

  // In "remaining only" mode, show just the sections/fields the extraction left
  // blank. Filled values stay in formData and are still submitted — they're only
  // hidden from view so the mentor focuses on the gaps.
  const visibleSections = scopeFormSchema.sections
    .map((section) => {
      const repeatable = isRepeatableSection(section);
      if (!remainingOnly) return { section, repeatable, fields: section.fields };
      if (repeatable) {
        return emptySnapshot.repeatable[section.section]
          ? { section, repeatable, fields: section.fields }
          : null;
      }
      const emptyNames = emptySnapshot.fixed[section.section];
      const fields = section.fields.filter((f) => emptyNames?.has(f.name));
      return fields.length ? { section, repeatable, fields } : null;
    })
    .filter(Boolean);

  const nothingRemaining = remainingOnly && visibleSections.length === 0;

  return (
    <form className={styles.scopeForm} onSubmit={handleSubmit}>
      {nothingRemaining ? (
        <div className={styles.allFilled}>
          <span className={styles.allFilledIcon}>✓</span>
          <p>Everything was filled in from the document — nothing left for you to complete. Review the file, then submit.</p>
        </div>
      ) : (
        visibleSections.map(({ section, repeatable, fields }, sectionIndex) => (
          <div key={sectionIndex} className={styles.section}>
            <h3 className={styles.sectionTitle}>{section.section}</h3>

            {repeatable ? (
              renderRepeatableSection(section)
            ) : (
              <div className={styles.fieldsGrid}>
                {fields.map((field, fieldIndex) => (
                  <div
                    key={fieldIndex}
                    className={`${styles.fieldGroup} ${
                      field.type === 'textarea' || field.type === 'multi-select tags' || field.type === 'multi-select checkboxes' || field.type === 'editable checklist'
                        ? styles.fieldFull
                        : ''
                    }`}
                  >
                    <label className={styles.fieldLabel}>{field.name}</label>
                    {renderField(field, section.section)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      <div className={styles.formActions}>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit">Submit Scope</Button>
      </div>
    </form>
  );
};

export default ScopeForm;
